import { Row, RowData } from '@tanstack/react-table';
import { faChevronRight } from '@fortawesome/pro-regular-svg-icons/faChevronRight';
import { FaIcon } from '../FaIcon';
import { cn } from '../../utils';

type TableRowExpandBtnProps<TData extends RowData> = {
  row: Row<TData>;
  label: string;
};

/**
 * Expand/collapse button for table rows with sub-content.
 *
 * Implements WAI-ARIA accordion trigger pattern:
 * - `aria-expanded` reflects current state
 * - `aria-controls` references the panel element (`row-{rowId}-panel`)
 * - `aria-label` provides accessible name
 *
 * The matching panel element is rendered by `<Table>` when
 * `renderSubComponent` is provided — it sets `id`, `role="region"`,
 * and `aria-labelledby` automatically.
 */
export const TableRowExpandBtn = <TData extends RowData>({
  row,
  label,
}: TableRowExpandBtnProps<TData>) => (
  <button
    id={`row-${row.id}-expand-btn`}
    className="lf:l-btn lf:l-btn-md lf:l-btn-tertiary lf:l-icon-btn"
    aria-expanded={row.getIsExpanded()}
    aria-controls={`row-${row.id}-panel`}
    aria-label={label}
    onClick={row.getToggleExpandedHandler()}
  >
    <FaIcon
      icon={faChevronRight}
      width={12}
      height={12}
      className={cn('lf:transition-transform', row.getIsExpanded() && 'lf:rotate-90')}
    />
  </button>
);
